import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Webcam from 'react-webcam';
import './FundTransferKeypad.css';

// Chargement automatique et garanti de la librairie jsQR
const loadJsQR = () => {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  return new Promise((resolve) => {
    if (document.getElementById('jsqr-script')) {
      const interval = setInterval(() => {
        if (window.jsQR) {
          clearInterval(interval);
          resolve(window.jsQR);
        }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.id = 'jsqr-script';
    script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
    script.onload = () => resolve(window.jsQR);
    script.onerror = () => resolve(null);
    document.body.appendChild(script);
  });
};

// Bip sonore de confirmation Web Audio API (sans fichier externe)
const playSuccessBeep = () => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, audioCtx.currentTime); // Note A5 claire
    gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.2);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.2);
  } catch (e) {}
};

const FundTransferMobilePortal = () => {
  // Authentication State
  const [currentUser, setCurrentUser] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [loadingAuth, setLoadingAuth] = useState(false);

  // Navigation: 'transfer' (1. Don) | 'scan_reception' (2. Scanner Réception) | 'refund' (3. Remise Billets) | 'dashboard' (4. Suivi)
  const [activeMode, setActiveMode] = useState('transfer');
  const [transferStep, setTransferStep] = useState(1);

  // Transfer State (Don Pièces)
  const [amountStr, setAmountStr] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [generatedTransfer, setGeneratedTransfer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Scanner State
  const [scannerActive, setScannerActive] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('environment'); // 'environment' = arrière
  const [scanFeedback, setScanFeedback] = useState(null);
  const [manualCodeInput, setManualCodeInput] = useState('');
  const [processingScan, setProcessingScan] = useState(false);
  const webcamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Remise Billets State
  const [selectedTransferForRefund, setSelectedTransferForRefund] = useState(null);
  const [refundAmountStr, setRefundAmountStr] = useState('');
  const [activeRefundQR, setActiveRefundQR] = useState(null);

  // Data & Balances
  const [transfers, setTransfers] = useState([]);
  const [realtimeBalances, setRealtimeBalances] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

const FALLBACK_TWIN_PARKS = [
  { name: "Twin Park - Tunisia Mall", category: "Twin Park" },
  { name: "Twin Park- Manar City", category: "Twin Park" },
  { name: "Twin park - Jardins d'eL Menzah", category: "Twin Park" },
  { name: "Jump Zone Mall of Djerba", category: "Twin Park" }
];

  // Admin Transfer Edit & Delete States
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [locations, setLocations] = useState(FALLBACK_TWIN_PARKS);

  useEffect(() => {
    loadJsQR();

    const savedToken = localStorage.getItem('ft_token') || localStorage.getItem('token');
    const savedUser = localStorage.getItem('ft_user') || localStorage.getItem('user');
    if (savedToken && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setCurrentUser(parsed);
      } catch (e) {}
    }
    fetchPortalData();
    fetchLocations();

    // Vérifier les paramètres d'URL pour le scan direct
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('receiveToken') || params.get('token') || params.get('receiveId');
    if (tokenParam) {
      setTimeout(() => processScannedPayload(tokenParam), 800);
    }

    const interval = setInterval(fetchPortalData, 6000);
    return () => clearInterval(interval);
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get('/api/fund-transfers/twin-parks', getHeaders());
      if (res.data?.locations && res.data.locations.length > 0) {
        setLocations(res.data.locations);
      } else {
        const allLocs = await axios.get('/api/Locations/getAllLocations', getHeaders()).catch(() => null);
        if (allLocs?.data && Array.isArray(allLocs.data) && allLocs.data.length > 0) {
          const twinOnly = allLocs.data.filter(l => l.category === 'Twin Park' || (l.name && l.name.toLowerCase().includes('twin')));
          setLocations(twinOnly.length > 0 ? twinOnly : allLocs.data);
        }
      }
    } catch (e) {
      setLocations(FALLBACK_TWIN_PARKS);
    }
  };



  const getHeaders = () => {
    const token = localStorage.getItem('ft_token') || localStorage.getItem('token');
    return token
      ? { headers: { Authorised: token, Authorization: `Bearer ${token}` } }
      : {};
  };

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchPortalData = async () => {
    try {
      const [trRes, balRes] = await Promise.all([
        axios.get('/api/fund-transfers', getHeaders()).catch(() => ({ data: { transfers: [] } })),
        axios.get('/api/fund-transfers/balances/realtime', getHeaders()).catch(() => ({ data: null }))
      ]);

      if (trRes.data?.transfers) {
        setTransfers(trRes.data.transfers);
      }
      if (balRes.data) {
        setRealtimeBalances(balRes.data);
      }
    } catch (err) {
      console.error('Erreur chargement données:', err);
    }
  };

  // -------------------------------------------------------------
  // AUTHENTIFICATION PAR PIN 4 CHIFFRES
  // -------------------------------------------------------------
  const handlePinKeyPress = (digit) => {
    if (pinInput.length < 4) {
      const nextPin = pinInput + digit;
      setPinInput(nextPin);
      setPinError('');
      if (nextPin.length === 4) {
        verifyPinAndLogin(nextPin);
      }
    }
  };

  const verifyPinAndLogin = async (pin) => {
    setLoadingAuth(true);
    setPinError('');
    try {
      const res = await axios.post('/api/fund-transfers/public-pin-auth', { pinCode: pin });
      if (res.data?.success) {
        localStorage.setItem('ft_token', res.data.token);
        localStorage.setItem('ft_user', JSON.stringify(res.data.user));
        setCurrentUser(res.data.user);
        setPinInput('');
        showNotification(`👋 Bienvenue ${res.data.user.name} (${res.data.user.localisation || 'Administration'})`);
        fetchPortalData();
      } else {
        setPinError(res.data?.msg || 'Code PIN incorrect');
        setPinInput('');
      }
    } catch (err) {
      setPinError(err.response?.data?.msg || 'Code PIN invalide');
      setPinInput('');
    } finally {
      setLoadingAuth(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ft_token');
    localStorage.removeItem('ft_user');
    setCurrentUser(null);
    setPinInput('');
    setPinError('');
  };

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'useradmin';

  // -------------------------------------------------------------
  // ACTIONS ADMIN : MODIFICATION & SUPPRESSION OPÉRATIONS
  // -------------------------------------------------------------
  const handleStartEditTransfer = (tr) => {
    setEditingTransfer(tr);
    setEditAmount(tr.amountTotal.toString());
    setEditNotes(tr.notes || '');
    setEditLocation(tr.toLocation || '');
  };

  const handleSaveEditTransfer = async () => {
    if (!editingTransfer) return;
    const num = parseFloat(editAmount);
    if (!num || num <= 0) {
      showNotification('Veuillez saisir un montant supérieur à 0 DT', 'error');
      return;
    }
    if (num < (editingTransfer.totalRefundedBills || 0)) {
      showNotification(`Le montant ne peut pas être inférieur à ${(editingTransfer.totalRefundedBills || 0).toFixed(3)} DT déjà remboursés`, 'error');
      return;
    }

    setSubmittingEdit(true);
    try {
      const res = await axios.put(`/api/fund-transfers/${editingTransfer._id}`, {
        amountTotal: num,
        notes: editNotes,
        toLocation: editLocation
      }, getHeaders());

      if (res.data?.success) {
        showNotification(`Opération ${editingTransfer.transferNumber} modifiée avec succès`);
        setEditingTransfer(null);
        fetchPortalData();
      }
    } catch (err) {
      showNotification(err.response?.data?.msg || 'Erreur modification opération', 'error');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleDeleteTransfer = async (tr) => {
    if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir SUPPRIMER définitivement l'opération ${tr.transferNumber} (${tr.amountTotal.toFixed(3)} DT) ?`)) {
      return;
    }

    try {
      const res = await axios.delete(`/api/fund-transfers/${tr._id}`, getHeaders());
      if (res.data?.success) {
        showNotification(`🗑️ Opération ${tr.transferNumber} supprimée`);
        fetchPortalData();
      }
    } catch (err) {
      showNotification(err.response?.data?.msg || 'Erreur suppression opération', 'error');
    }
  };


  // -------------------------------------------------------------
  // GESTION DU PAVÉ NUMÉRIQUE
  // -------------------------------------------------------------
  const handleNumPress = (val, setter, currentStr) => {
    if (val === '.') {
      if (!currentStr.includes('.')) {
        setter(prev => (prev === '' ? '0.' : prev + '.'));
      }
    } else {
      if (currentStr === '0') {
        setter(val);
      } else {
        if (currentStr.includes('.')) {
          const parts = currentStr.split('.');
          if (parts[1] && parts[1].length >= 3) return;
        }
        setter(prev => prev + val);
      }
    }
  };

  const numericAmount = parseFloat(amountStr) || 0;

  // -------------------------------------------------------------
  // ÉTAPE 1 : CRÉATION DU DON DE PIÈCES (ADMIN / DONNEUR)
  // -------------------------------------------------------------
  const handleGenerateQR = async () => {
    if (numericAmount <= 0) {
      alert('Veuillez saisir un montant en pièces supérieur à 0 DT.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        amountTotal: numericAmount,
        notes: transferNotes
      };

      const res = await axios.post('/api/fund-transfers', payload, getHeaders());
      if (res.data?.success) {
        setGeneratedTransfer(res.data.transfer);
        setTransferStep(2);
        fetchPortalData();
        showNotification(`🎉 Bordereau QR généré (${numericAmount.toFixed(3)} DT en pièces)`);
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // ÉTAPE 2 & 3 : MOTEUR DE DÉCODAGE & VALIDATION DU SCAN QR
  // -------------------------------------------------------------
  const processScannedPayload = async (rawCode) => {
    if (!rawCode || processingScan) return;
    setProcessingScan(true);

    let extractedToken = rawCode;
    let scanType = 'UNKNOWN';
    let transferId = null;
    let refundAmount = null;

    try {
      if (typeof rawCode === 'string' && rawCode.trim().startsWith('{')) {
        const parsed = JSON.parse(rawCode);
        extractedToken = parsed.qrToken || parsed.transferId || parsed.transferNumber;
        scanType = parsed.type || 'COINS_EMISSION';
        transferId = parsed.transferId || parsed.qrToken;
        refundAmount = parsed.refundAmount !== undefined ? parsed.refundAmount : parsed.amount;
      } else if (typeof rawCode === 'string' && rawCode.includes('receiveToken=')) {
        const urlParams = new URLSearchParams(rawCode.split('?')[1]);
        extractedToken = urlParams.get('receiveToken');
        scanType = 'COINS_EMISSION';
      }
    } catch (e) {
      extractedToken = rawCode;
    }

    const isRefund =
      scanType === 'REFUND_BILLS' ||
      (typeof rawCode === 'string' && rawCode.includes('REFUND_BILLS')) ||
      (typeof rawCode === 'string' && rawCode.includes('REM-')) ||
      activeMode === 'scan_refund';

    // 1. Scan Remise Billets (Admin scanne le QR du Parc pour solder/réduire le solde dû)
    if (isRefund) {
      try {
        const payload = {
          qrToken: extractedToken,
          transferId: transferId || extractedToken
        };
        if (refundAmount !== null && refundAmount !== undefined && !isNaN(parseFloat(refundAmount))) {
          payload.refundAmount = parseFloat(refundAmount);
        }

        const res = await axios.post('/api/fund-transfers/confirm-refund-scan', payload, getHeaders());

        if (res.data?.success) {
          playSuccessBeep();
          showNotification(res.data.msg);
          setScanFeedback({ type: 'success', msg: res.data.msg });
          setScannerActive(false);
          fetchPortalData();
          setTimeout(() => {
            setActiveMode('dashboard');
            setScanFeedback(null);
          }, 1400);
        }
      } catch (err) {
        const errMsg = err.response?.data?.msg || 'Erreur validation remise en billets';
        setScanFeedback({ type: 'error', msg: errMsg });
        showNotification(errMsg, 'error');
      } finally {
        setProcessingScan(false);
      }
      return;
    }

    // 2. Scan Réception Pièces (Parc Récepteur scanne le QR de Don émis par l'Admin)
    try {
      const res = await axios.post(
        `/api/fund-transfers/${extractedToken}/confirm-reception`,
        {
          receiverLocation: currentUser?.localisation || 'Twin Park',
          receiverName: currentUser?.name || 'Caissier'
        },
        getHeaders()
      );

      if (res.data?.success) {
        playSuccessBeep();
        showNotification(res.data.msg);
        setScanFeedback({ type: 'success', msg: res.data.msg });
        setScannerActive(false);
        fetchPortalData();
        setTimeout(() => {
          setActiveMode('dashboard');
          setScanFeedback(null);
        }, 1400);
      }
    } catch (err) {
      const errMsg = err.response?.data?.msg || 'Erreur ou bordereau déjà réceptionné';
      setScanFeedback({ type: 'error', msg: errMsg });
      showNotification(errMsg, 'error');
    } finally {
      setProcessingScan(false);
    }
  };

  const handleGenerateRefundQR = async () => {
    if (!selectedTransferForRefund) return;
    const amountNum = parseFloat(refundAmountStr);
    if (!amountNum || amountNum <= 0) {
      alert('Veuillez saisir un montant de remise valide supérieur à 0 DT.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `/api/fund-transfers/${selectedTransferForRefund._id}/generate-refund-qr`,
        { refundAmount: amountNum },
        getHeaders()
      );
      if (res.data?.success) {
        setActiveRefundQR({
          transfer: res.data.transfer,
          amount: res.data.refundAmount,
          qrCodeDataUrl: res.data.refundQrCodeDataUrl
        });
        showNotification(`💵 QR Code de remise généré (${res.data.refundAmount.toFixed(3)} DT en billets)`);
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Erreur génération QR remise');
    } finally {
      setSubmitting(false);
    }
  };

  // Détection continue par caméra (jsQR + BarcodeDetector)
  useEffect(() => {
    if (!scannerActive) {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      return;
    }

    const hasBarcodeDetector = 'BarcodeDetector' in window;
    const barcodeDetector = hasBarcodeDetector ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;

    scanIntervalRef.current = setInterval(async () => {
      if (webcamRef.current?.video?.readyState === 4 && !processingScan) {
        const video = webcamRef.current.video;

        // Méthode 1: BarcodeDetector natif
        if (barcodeDetector) {
          try {
            const codes = await barcodeDetector.detect(video);
            if (codes && codes.length > 0 && codes[0].rawValue) {
              clearInterval(scanIntervalRef.current);
              processScannedPayload(codes[0].rawValue);
              return;
            }
          } catch (e) {}
        }

        // Méthode 2: Décodage jsQR sur Canvas (Universel sur tous navigateurs/téléphones)
        if (window.jsQR) {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 320;
            canvas.height = video.videoHeight || 240;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: 'dontInvert'
              });
              if (code && code.data) {
                clearInterval(scanIntervalRef.current);
                processScannedPayload(code.data);
                return;
              }
            }
          } catch (e) {}
        }
      }
    }, 280);

    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    };
  }, [scannerActive, processingScan, activeMode]);

  // Décodage depuis une photo / image capturée
  const handleImageUploadScan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        if ('BarcodeDetector' in window) {
          try {
            const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
            const codes = await detector.detect(img);
            if (codes && codes.length > 0) {
              processScannedPayload(codes[0].rawValue);
              return;
            }
          } catch (err) {}
        }

        if (window.jsQR) {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = window.jsQR(imageData.data, imageData.width, imageData.height);
          if (code && code.data) {
            processScannedPayload(code.data);
            return;
          }
        }
        alert('Aucun QR code lisible détecté sur cette image. Veuillez réessayer.');
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const activeTransfersList = (transfers || []).filter(
    (t) => t.status !== 'SETTLED_CANCELLED' && t.status !== 'CANCELLED' && (t.status === 'ACTIVE_DUE' || t.status === 'PARTIALLY_REFUNDED') && (Number(t.remainingBalance) || 0) > 0
  );
  const ongoingTransfers = (transfers || []).filter(
    (t) => t.status !== 'SETTLED_CANCELLED' && t.status !== 'CANCELLED' && ((Number(t.remainingBalance) || 0) > 0 || t.status === 'PENDING_RECEPTION')
  );
  const settledTransfers = (transfers || []).filter(
    (t) => t.status === 'SETTLED_CANCELLED' || t.status === 'CANCELLED' || (t.status !== 'PENDING_RECEPTION' && (Number(t.remainingBalance) || 0) <= 0.001)
  );
  const summary = realtimeBalances?.summary || {};

  const keyBtnStyle = {
    width: '100%',
    height: '85px',
    minHeight: '85px',
    maxHeight: '85px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '34px',
    fontWeight: '900',
    borderRadius: '20px',
    border: '3px solid #cbd5e1',
    background: '#ffffff',
    color: '#0f172a',
    boxSizing: 'border-box',
    cursor: 'pointer',
    margin: 0,
    padding: 0,
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  };

  const keypadGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    width: '100%',
    margin: '0 0 16px 0',
    boxSizing: 'border-box'
  };

  // =============================================================
  // VUE 0 : CONNEXION PAR PIN
  // =============================================================
  if (!currentUser) {
    return (
      <div id="ft-app-wrapper" className="tk-transfer-portal">
        <div className="ft-card" style={{ textAlign: 'center', padding: '30px 18px' }}>
          <div style={{ fontSize: 50, marginBottom: 8 }}>🔐</div>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: '#1e3a8a', margin: '0 0 6px 0' }}>TASKO POS</h2>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
            Transferts Pièces & Remise Billets
          </p>

          <div style={{ background: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: '16px', padding: '14px', marginBottom: '20px' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#1d4ed8', marginBottom: 8 }}>
              Composez votre Code PIN (4 chiffres) :
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', margin: '12px 0' }}>
              {[0, 1, 2, 3].map((idx) => (
                <div
                  key={idx}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '3px solid #94a3b8',
                    background: pinInput.length > idx ? '#2563eb' : 'transparent',
                    borderColor: pinInput.length > idx ? '#2563eb' : '#94a3b8',
                    transform: pinInput.length > idx ? 'scale(1.2)' : 'scale(1)',
                    transition: 'all 0.15s ease'
                  }}
                />
              ))}
            </div>
            {pinError && <div style={{ color: '#dc2626', fontWeight: 800, fontSize: 13, marginTop: 6 }}>{pinError}</div>}
          </div>

          <div style={keypadGridStyle} className="ft-keypad-grid">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button
                key={d}
                type="button"
                style={keyBtnStyle}
                className="ft-num-key"
                onClick={() => handlePinKeyPress(d)}
                disabled={loadingAuth}
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              style={{ ...keyBtnStyle, background: '#fef2f2', borderColor: '#fca5a5', color: '#dc2626', fontSize: '15px' }}
              className="ft-num-key clear-btn"
              onClick={() => setPinInput('')}
              disabled={loadingAuth}
            >
              Effacer
            </button>
            <button
              type="button"
              style={keyBtnStyle}
              className="ft-num-key"
              onClick={() => handlePinKeyPress('0')}
              disabled={loadingAuth}
            >
              0
            </button>
            <button
              type="button"
              style={{ ...keyBtnStyle, background: '#f1f5f9', color: '#dc2626', fontSize: '28px' }}
              className="ft-num-key delete"
              onClick={() => setPinInput(prev => prev.slice(0, -1))}
              disabled={loadingAuth}
            >
              ⌫
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =============================================================
  // VUE PRINCIPALE CONNECTÉE
  // =============================================================
  return (
    <div id="ft-app-wrapper" className="tk-transfer-portal">
      {/* Toast Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '99px',
          fontWeight: 900,
          fontSize: '14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
        }}>
          {notification.msg}
        </div>
      )}

      {/* Header avec Identité Connectée */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#ffffff',
        padding: '14px 16px',
        borderRadius: '20px',
        border: '2px solid #e2e8f0',
        marginBottom: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
      }}>
        <div>
          <div style={{ fontWeight: 900, color: '#1e3a8a', fontSize: '15px' }}>
            👤 {currentUser.name}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>
            📍 {currentUser.localisation || 'Administration'}
          </div>
        </div>
        <button
          type="button"
          style={{
            background: '#fef2f2',
            border: '1.5px solid #fecaca',
            color: '#dc2626',
            fontWeight: 800,
            padding: '6px 14px',
            borderRadius: '99px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          onClick={handleLogout}
        >
          🔒 Déconnexion
        </button>
      </div>

      {/* 4 Onglets Tactiles en Grille 2x2 */}
      <div className="ft-tabs-row">
        <button
          type="button"
          className={`ft-tab-btn ${activeMode === 'transfer' ? 'active' : ''}`}
          onClick={() => {
            setActiveMode('transfer');
            setTransferStep(1);
            setScannerActive(false);
          }}
        >
          <span className="ft-tab-icon">🪙</span>
          <span>1. Donner Pièces</span>
        </button>

        <button
          type="button"
          className={`ft-tab-btn ${activeMode === 'scan_reception' ? 'active' : ''}`}
          onClick={() => {
            setActiveMode('scan_reception');
            setScannerActive(true);
            setScanFeedback(null);
          }}
        >
          <span className="ft-tab-icon">📷</span>
          <span>2. Scanner Réception</span>
        </button>

        <button
          type="button"
          className={`ft-tab-btn ${activeMode === 'refund' ? 'active' : ''}`}
          onClick={() => {
            setActiveMode('refund');
            setSelectedTransferForRefund(null);
            setActiveRefundQR(null);
            setScannerActive(false);
            fetchPortalData();
          }}
        >
          <span className="ft-tab-icon">💵</span>
          <span>3. Remise Billets</span>
          {activeTransfersList.length > 0 && (
            <span style={{ background: '#f59e0b', color: '#000000', fontSize: 10, padding: '2px 7px', borderRadius: 99, fontWeight: 900 }}>
              {activeTransfersList.length}
            </span>
          )}
        </button>

        <button
          type="button"
          className={`ft-tab-btn ${activeMode === 'dashboard' ? 'active' : ''}`}
          onClick={() => {
            setActiveMode('dashboard');
            setScannerActive(false);
            fetchPortalData();
          }}
        >
          <span className="ft-tab-icon">📊</span>
          <span>4. Mon Suivi</span>
        </button>
      </div>

      {/* ========================================================= */}
      {/* ── MODE 1: DON DE PIÈCES ── */}
      {/* ========================================================= */}
      {activeMode === 'transfer' && (
        <>
          {transferStep === 1 && (
            <div className="ft-card">
              <div className="ft-admin-banner">
                <span>🏛️ Émetteur : {currentUser.name}</span>
                <span>🪙 Don en Pièces</span>
              </div>

              <div className="ft-lcd-screen">
                <div className="ft-lcd-header">
                  <span>Montant en Pièces</span>
                  <span>🪙 PIÈCES DE MONNAIE</span>
                </div>
                <div className="ft-lcd-number">
                  {amountStr || '0'}
                  <span className="ft-lcd-currency">DT</span>
                </div>
              </div>

              <div style={keypadGridStyle} className="ft-keypad-grid">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    style={keyBtnStyle}
                    className="ft-num-key"
                    onClick={() => handleNumPress(d, setAmountStr, amountStr)}
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  style={{ ...keyBtnStyle, fontSize: '40px', color: '#475569' }}
                  className="ft-num-key dot"
                  onClick={() => handleNumPress('.', setAmountStr, amountStr)}
                >
                  .
                </button>
                <button
                  type="button"
                  style={keyBtnStyle}
                  className="ft-num-key"
                  onClick={() => handleNumPress('0', setAmountStr, amountStr)}
                >
                  0
                </button>
                <button
                  type="button"
                  style={{ ...keyBtnStyle, background: '#f1f5f9', color: '#dc2626', fontSize: '28px' }}
                  className="ft-num-key delete"
                  onClick={() => setAmountStr(prev => prev.slice(0, -1))}
                >
                  ⌫
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#f8fafc',
                    border: '1.5px dashed #cbd5e1',
                    borderRadius: '12px',
                    fontWeight: 800,
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                  onClick={() => setAmountStr('')}
                >
                  🗑️ Effacer le montant
                </button>
              </div>

              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '14px',
                padding: '12px',
                fontSize: '13px',
                color: '#1e40af',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>ℹ️</span>
                <div>
                  <strong>Localisation automatique :</strong> Le parc sera affecté dès que le caissier du parc scannera votre QR Code avec son compte.
                </div>
              </div>

              <button
                type="button"
                className="ft-btn-action primary"
                onClick={handleGenerateQR}
                disabled={submitting || numericAmount <= 0}
              >
                {submitting ? '⏳ Création...' : `🚀 Générer QR Code (${numericAmount.toFixed(3)} DT)`}
              </button>
            </div>
          )}

          {transferStep === 2 && generatedTransfer && (
            <div className="ft-qr-container">
              <div style={{
                background: '#ecfdf5',
                color: '#047857',
                padding: '6px 16px',
                borderRadius: '99px',
                display: 'inline-block',
                fontWeight: 900,
                fontSize: 13,
                marginBottom: 8
              }}>
                ✅ QR CODE DE DON GÉNÉRÉ
              </div>

              <h2 style={{ fontSize: 44, fontWeight: 900, color: '#2563eb', margin: '6px 0' }}>
                {generatedTransfer.amountTotal.toFixed(3)} <small style={{ fontSize: 24 }}>DT</small>
              </h2>

              <div style={{ fontSize: 14, color: '#64748b', fontWeight: 800, marginBottom: 12 }}>
                🪙 Pièces de monnaie en attente de scan par le parc
              </div>

              {generatedTransfer.qrCodeDataUrl && (
                <img
                  src={generatedTransfer.qrCodeDataUrl}
                  alt="QR Code Don de Pièces"
                  className="ft-qr-img"
                />
              )}

              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                color: '#92400e',
                padding: '12px',
                borderRadius: '16px',
                fontSize: 13,
                marginTop: 12,
                textAlign: 'left'
              }}>
                📱 <strong>Instructions :</strong> <br />
                Le caissier du parc ouvre <strong>« 2. Scanner Réception »</strong> et scanne ce QR Code pour enregistrer la réception.
              </div>

              <button
                type="button"
                className="ft-btn-action primary"
                style={{ marginTop: '16px' }}
                onClick={() => {
                  setAmountStr('');
                  setTransferStep(1);
                }}
              >
                ➕ Nouveau Don de Pièces
              </button>
            </div>
          )}
        </>
      )}

      {/* ========================================================= */}
      {/* ── MODE 2: SCANNER QR CODE (RÉCEPTION & REMISE) ── */}
      {/* ========================================================= */}
      {activeMode === 'scan_reception' && (
        <div className="ft-card">
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 900, margin: '0 0 4px 0', fontSize: '18px' }}>
              📷 Scannez le QR Code de Don de Pièces
            </h3>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
              Pointez la caméra vers l'écran pour valider la réception à : <br />
              <strong style={{ color: '#1d4ed8' }}>📍 {currentUser.localisation || 'Votre Parc'}</strong>
            </p>
          </div>

          {/* Viseur Caméra Live */}
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '360px',
            margin: '0 auto 16px auto',
            borderRadius: '20px',
            overflow: 'hidden',
            border: '4px solid #3b82f6',
            background: '#000000',
            aspectRatio: '1/1'
          }}>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: cameraFacing }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{
              position: 'absolute',
              top: '15%',
              left: '15%',
              width: '70%',
              height: '70%',
              border: '3px dashed #38bdf8',
              borderRadius: '16px',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
              pointerEvents: 'none'
            }} />
          </div>

          {/* Contrôles Scanner */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '16px' }}>
            <button
              type="button"
              style={{
                background: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                borderRadius: '12px',
                padding: '8px 14px',
                fontWeight: 800,
                fontSize: 12,
                cursor: 'pointer'
              }}
              onClick={() => setCameraFacing(prev => prev === 'environment' ? 'user' : 'environment')}
            >
              🔄 Changer Caméra ({cameraFacing === 'environment' ? 'Arrière' : 'Avant'})
            </button>

            <label style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '12px',
              padding: '8px 14px',
              fontWeight: 800,
              fontSize: 12,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🖼️ Photo / Galerie
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUploadScan}
              />
            </label>
          </div>

          {/* Feedback Scan */}
          {scanFeedback && (
            <div style={{
              padding: '12px',
              borderRadius: '14px',
              fontWeight: 800,
              fontSize: 13,
              marginBottom: 16,
              background: scanFeedback.type === 'success' ? '#ecfdf5' : '#fef2f2',
              color: scanFeedback.type === 'success' ? '#047857' : '#dc2626',
              border: `1.5px solid ${scanFeedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`
            }}>
              {scanFeedback.msg}
            </div>
          )}

          {/* Saisie Manuelle de Secours */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 6 }}>
              Ou saisissez la référence du bordereau :
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '2px solid #cbd5e1',
                  fontSize: 13,
                  boxSizing: 'border-box'
                }}
                placeholder="Ex: TRF-... ou FT_..."
                value={manualCodeInput}
                onChange={(e) => setManualCodeInput(e.target.value)}
              />
              <button
                type="button"
                className="ft-btn-action primary"
                style={{ minHeight: 46, width: 'auto', padding: '0 16px', fontSize: 13 }}
                onClick={() => processScannedPayload(manualCodeInput.trim())}
                disabled={!manualCodeInput.trim() || processingScan}
              >
                {processingScan ? '⏳' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* ── MODE 3: REMISE DE BILLETS (PARC ➡️ ADMIN) ── */}
      {/* ========================================================= */}
      {activeMode === 'refund' && (
        <div className="ft-card">
          {!selectedTransferForRefund && !activeRefundQR ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontWeight: 900, margin: 0, fontSize: '18px' }}>💵 Remise de Billets à l'Admin</h3>
                <button
                  type="button"
                  style={{
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '10px',
                    fontWeight: 800,
                    fontSize: 12,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setActiveMode('scan_reception');
                    setScannerActive(true);
                  }}
                >
                  📷 Scanner QR
                </button>
              </div>

              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                Sélectionnez une opération pour générer le QR Code de remise en billets :
              </p>

              {activeTransfersList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 10px', color: '#64748b' }}>
                  <div style={{ fontSize: 48 }}>✅</div>
                  <h4 style={{ fontWeight: 900, marginTop: 8 }}>Toutes les opérations sont soldées !</h4>
                  <p style={{ fontSize: 13 }}>Aucun montant de pièces en attente de remboursement en billets.</p>
                </div>
              ) : (
                activeTransfersList.map((tr) => (
                  <div key={tr._id} className="ft-tracking-card">
                    <div className="ft-card-top-row">
                      <div className="ft-park-title">
                        <span>🎢</span>
                        <span>{tr.toLocation}</span>
                      </div>
                      <span className="ft-status-pill partial">
                        ⏳ Reste dû : {tr.remainingBalance.toFixed(3)} DT
                      </span>
                    </div>

                    <div className="ft-metrics-grid">
                      <div className="ft-metric-box coins">
                        <span className="ft-metric-label">🪙 Don Initial</span>
                        <span className="ft-metric-value">{tr.amountTotal.toFixed(3)} DT</span>
                      </div>
                      <div className="ft-metric-box refunded">
                        <span className="ft-metric-label">💵 Déjà Remis</span>
                        <span className="ft-metric-value">{tr.totalRefundedBills.toFixed(3)} DT</span>
                      </div>
                      <div className="ft-metric-box due">
                        <span className="ft-metric-label">🔴 Reste à Remettre</span>
                        <span className="ft-metric-value">{tr.remainingBalance.toFixed(3)} DT</span>
                      </div>
                    </div>

                    <div className="ft-progress-track">
                      <div
                        className="ft-progress-fill"
                        style={{ width: `${Math.min(100, (tr.totalRefundedBills / tr.amountTotal) * 100)}%` }}
                      />
                    </div>

                    <div className="ft-dates-block">
                      <div className="ft-date-item">
                        <span className="ft-date-tag">📥 Reçu le :</span>
                        <span className="ft-date-text success">
                          {tr.receivedDate ? new Date(tr.receivedDate).toLocaleString() : 'En attente de scan'}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="ft-btn-action success"
                      style={{ minHeight: 52, fontSize: 14, marginTop: 4 }}
                      onClick={() => {
                        setSelectedTransferForRefund(tr);
                        setRefundAmountStr(tr.remainingBalance.toString());
                      }}
                    >
                      📱 Générer QR Code Remise Billets ({tr.remainingBalance.toFixed(3)} DT)
                    </button>
                  </div>
                ))
              )}
            </>
          ) : selectedTransferForRefund && !activeRefundQR ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ fontWeight: 900, margin: 0, fontSize: '18px' }}>💵 Montant Remis en Billets</h3>
                <button
                  type="button"
                  style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 800 }}
                  onClick={() => setSelectedTransferForRefund(null)}
                >
                  ✕ Annuler
                </button>
              </div>

              <div style={{ background: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: 16, padding: 12, fontSize: 13, marginBottom: 16 }}>
                <div><strong>Parc :</strong> 🎢 {selectedTransferForRefund.toLocation}</div>
                <div><strong>Don initial en pièces :</strong> {selectedTransferForRefund.amountTotal.toFixed(3)} DT</div>
                <div><strong>Reste à rembourser :</strong> <span style={{ color: '#dc2626', fontWeight: 900 }}>{selectedTransferForRefund.remainingBalance.toFixed(3)} DT</span></div>
              </div>

              <div className="ft-lcd-screen">
                <div className="ft-lcd-header">
                  <span>Montant en Billets</span>
                  <span>💵 BILLETS DE BANQUE</span>
                </div>
                <div className="ft-lcd-number">
                  {refundAmountStr || '0'}
                  <span className="ft-lcd-currency">DT</span>
                </div>
              </div>

              <div style={keypadGridStyle} className="ft-keypad-grid">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    style={keyBtnStyle}
                    className="ft-num-key"
                    onClick={() => handleNumPress(d, setRefundAmountStr, refundAmountStr)}
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  style={{ ...keyBtnStyle, fontSize: '40px', color: '#475569' }}
                  className="ft-num-key dot"
                  onClick={() => handleNumPress('.', setRefundAmountStr, refundAmountStr)}
                >
                  .
                </button>
                <button
                  type="button"
                  style={keyBtnStyle}
                  className="ft-num-key"
                  onClick={() => handleNumPress('0', setRefundAmountStr, refundAmountStr)}
                >
                  0
                </button>
                <button
                  type="button"
                  style={{ ...keyBtnStyle, background: '#f1f5f9', color: '#dc2626', fontSize: '28px' }}
                  className="ft-num-key delete"
                  onClick={() => setRefundAmountStr(prev => prev.slice(0, -1))}
                >
                  ⌫
                </button>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="ft-btn-action outline"
                  style={{ maxWidth: '100px' }}
                  onClick={() => setSelectedTransferForRefund(null)}
                >
                  Retour
                </button>
                <button
                  type="button"
                  className="ft-btn-action success"
                  onClick={handleGenerateRefundQR}
                  disabled={submitting || !parseFloat(refundAmountStr) || parseFloat(refundAmountStr) <= 0}
                >
                  {submitting ? '⏳ Génération...' : `📱 Générer QR Code Remise (${parseFloat(refundAmountStr || 0).toFixed(3)} DT)`}
                </button>
              </div>
            </>
          ) : (
            <div className="ft-qr-container">
              <div style={{
                background: '#ecfdf5',
                color: '#047857',
                padding: '6px 16px',
                borderRadius: '99px',
                display: 'inline-block',
                fontWeight: 900,
                fontSize: 13,
                marginBottom: 8
              }}>
                💵 QR CODE DE REMISE EN BILLETS
              </div>

              <h2 style={{ fontSize: 40, fontWeight: 900, color: '#059669', margin: '6px 0' }}>
                {activeRefundQR.amount.toFixed(3)} <small style={{ fontSize: 22 }}>DT</small>
              </h2>

              <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                🎢 {activeRefundQR.transfer.toLocation} ➡️ 🏛️ Administration
              </div>

              {activeRefundQR.qrCodeDataUrl || activeRefundQR.transfer?.refundQrCodeDataUrl || activeRefundQR.transfer?.qrCodeDataUrl ? (
                <img
                  src={activeRefundQR.qrCodeDataUrl || activeRefundQR.transfer?.refundQrCodeDataUrl || activeRefundQR.transfer?.qrCodeDataUrl}
                  alt="QR Code Remise Billets"
                  className="ft-qr-img"
                />
              ) : null}

              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                color: '#92400e',
                padding: '12px',
                borderRadius: '16px',
                fontSize: 13,
                marginTop: 12,
                textAlign: 'left'
              }}>
                📱 <strong>Instructions :</strong> <br />
                L'Admin scanne ce QR Code pour enregistrer la remise en billets et solder/annuler automatiquement l'opération.
              </div>

              <button
                type="button"
                className="ft-btn-action outline"
                style={{ marginTop: '16px' }}
                onClick={() => {
                  setActiveRefundQR(null);
                  setSelectedTransferForRefund(null);
                }}
              >
                ✕ Fermer le QR Code
              </button>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* ── MODE 4: MON SUIVI & SOLDES ── */}
      {/* ========================================================= */}
      {activeMode === 'dashboard' && (
        <>
          <div className="ft-summary-box">
            <div className="ft-summary-top">
              <span className="ft-summary-label">
                TOTAL RESTANT DÛ EN BILLETS (CIRCULATION)
              </span>
              <span style={{ background: '#f59e0b', color: '#000000', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 900 }}>
                En Cours
              </span>
            </div>
            <div className="ft-summary-main-val">
              {(summary.totalRemainingDueGlobal || 0).toFixed(3)} <small style={{ fontSize: 20, color: '#ffffff' }}>DT</small>
            </div>

            <div className="ft-summary-subgrid">
              <div className="ft-summary-subitem">
                <span className="ft-summary-subitem-title">🪙 Total Pièces Données</span>
                <span className="ft-summary-subitem-val">{(summary.totalCoinsGivenGlobal || 0).toFixed(3)} DT</span>
              </div>
              <div className="ft-summary-subitem">
                <span className="ft-summary-subitem-title">💵 Total Billets Remis</span>
                <span className="ft-summary-subitem-val" style={{ color: '#34d399' }}>{(summary.totalBillsRefundedGlobal || 0).toFixed(3)} DT</span>
              </div>
            </div>
          </div>

          <div className="ft-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 900, margin: 0, fontSize: '18px' }}>📜 Suivi des Opérations en Cours :</h3>
              <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 10px', borderRadius: 99, fontWeight: 900, fontSize: 12 }}>
                {ongoingTransfers.length} en cours
              </span>
            </div>

            {ongoingTransfers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 16px', color: '#64748b' }}>
                <div style={{ fontSize: 44 }}>✨</div>
                <h4 style={{ fontWeight: 900, color: '#0f172a', margin: '8px 0 4px 0' }}>Aucun transfert en cours</h4>
                <p style={{ fontSize: 13, margin: 0 }}>Toutes les opérations ont été soldées / annulées et éliminées du suivi actif.</p>
              </div>
            ) : (
              ongoingTransfers.map((tr) => (
                <div key={tr._id} className="ft-tracking-card">
                  <div className="ft-card-top-row">
                    <div className="ft-park-title">
                      <span>🎢</span>
                      <span>{tr.toLocation}</span>
                    </div>
                    {tr.status === 'PARTIALLY_REFUNDED' ? (
                      <span className="ft-status-pill partial">
                        ⏳ PARTIELLEMENT REMBOURSÉ
                      </span>
                    ) : tr.status === 'ACTIVE_DUE' ? (
                      <span className="ft-status-pill active">
                        🔴 REÇU (NON REMBOURSÉ)
                      </span>
                    ) : (
                      <span className="ft-status-pill pending">
                        ⏳ EN ATTENTE SCAN RÉCEPTION
                      </span>
                    )}
                  </div>

                  <div className="ft-metrics-grid">
                    <div className="ft-metric-box coins">
                      <span className="ft-metric-label">🪙 Don Initial</span>
                      <span className="ft-metric-value">{tr.amountTotal.toFixed(3)} DT</span>
                    </div>
                    <div className="ft-metric-box refunded">
                      <span className="ft-metric-label">💵 Déjà Remis</span>
                      <span className="ft-metric-value">{tr.totalRefundedBills.toFixed(3)} DT</span>
                    </div>
                    <div className="ft-metric-box due">
                      <span className="ft-metric-label">🔴 Reste Dû</span>
                      <span className="ft-metric-value">{tr.remainingBalance.toFixed(3)} DT</span>
                    </div>
                  </div>

                  <div className="ft-progress-track">
                    <div
                      className="ft-progress-fill"
                      style={{ width: `${Math.min(100, (tr.totalRefundedBills / tr.amountTotal) * 100)}%` }}
                    />
                  </div>

                  <div className="ft-dates-block">
                    <div className="ft-date-item">
                      <span className="ft-date-tag">📤 Émis le :</span>
                      <span className="ft-date-text">{new Date(tr.sentDate || tr.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="ft-date-item">
                      <span className="ft-date-tag">📥 Réception :</span>
                      {tr.receivedDate ? (
                        <span className="ft-date-text success">
                          ✅ {new Date(tr.receivedDate).toLocaleString()}
                          <span className="ft-agent-tag">par {tr.toUserName || tr.toLocation}</span>
                        </span>
                      ) : (
                        <span className="ft-date-text pending">
                          ⏳ En attente de scan par le parc
                        </span>
                      )}
                    </div>
                  </div>

                  {(tr.refundHistory || []).length > 0 && (
                    <div className="ft-history-section">
                      <div className="ft-history-title">
                        <span>💸</span>
                        <span>Historique des remises en billets validées :</span>
                      </div>
                      {tr.refundHistory.map((ref, idx) => (
                        <div key={idx} className="ft-history-item">
                          <span>📅 {new Date(ref.date).toLocaleString()} {ref.notes ? `- ${ref.notes}` : ''}</span>
                          <strong style={{ color: '#047857', fontSize: 12 }}>+{ref.amount.toFixed(3)} DT</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions Réservées Uniquement à l'Admin */}
                  {isAdmin && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 8,
                      borderTop: '1px dashed #e2e8f0',
                      paddingTop: 10,
                      marginTop: 6
                    }}>
                      <button
                        type="button"
                        onClick={() => handleStartEditTransfer(tr)}
                        style={{
                          background: '#f0f9ff',
                          border: '1.5px solid #bae6fd',
                          color: '#0284c7',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        ✏️ Modifier (Admin)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTransfer(tr)}
                        style={{
                          background: '#fef2f2',
                          border: '1.5px solid #fecaca',
                          color: '#dc2626',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Section Optionnelle des Opérations Soldées / Archivées */}
            {settledTransfers.length > 0 && (
              <div style={{ marginTop: 20, textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <button
                  type="button"
                  style={{
                    background: '#f8fafc',
                    border: '1.5px dashed #cbd5e1',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowArchived(!showArchived)}
                >
                  {showArchived
                    ? '🙈 Masquer les opérations soldées & annulées'
                    : `📦 Voir l'historique archivé (${settledTransfers.length} soldées)`}
                </button>

                {showArchived && (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {settledTransfers.map((tr) => (
                      <div
                        key={tr._id}
                        className="ft-tracking-card"
                        style={{ opacity: 0.75, background: '#f8fafc', borderColor: '#cbd5e1' }}
                      >
                        <div className="ft-card-top-row">
                          <span className="ft-park-title" style={{ color: '#475569' }}>
                            🎢 {tr.toLocation}
                          </span>
                          <span className="ft-status-pill settled">
                            ✅ TOTALEMENT SOLDÉ
                          </span>
                        </div>

                        <div className="ft-metrics-grid">
                          <div className="ft-metric-box coins">
                            <span className="ft-metric-label">🪙 Initial</span>
                            <span className="ft-metric-value">{tr.amountTotal.toFixed(3)} DT</span>
                          </div>
                          <div className="ft-metric-box refunded">
                            <span className="ft-metric-label">💵 Total Remboursé</span>
                            <span className="ft-metric-value">{tr.totalRefundedBills.toFixed(3)} DT</span>
                          </div>
                          <div className="ft-metric-box" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>
                            <span className="ft-metric-label" style={{ color: '#047857' }}>Reste Dû</span>
                            <span className="ft-metric-value" style={{ color: '#047857' }}>0.000 DT</span>
                          </div>
                        </div>

                        <div className="ft-dates-block">
                          <div className="ft-date-item">
                            <span className="ft-date-tag">📅 Dernière remise :</span>
                            <span className="ft-date-text">
                              {tr.lastRefundDate ? new Date(tr.lastRefundDate).toLocaleString() : new Date(tr.updatedAt).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {isAdmin && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 8,
                            borderTop: '1px dashed #e2e8f0',
                            paddingTop: 8,
                            marginTop: 4
                          }}>
                            <button
                              type="button"
                              onClick={() => handleDeleteTransfer(tr)}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#dc2626',
                                padding: '4px 10px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Supprimer de l'archive
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FundTransferMobilePortal;
